import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message, Modal, Button, Alert } from '@surecompanies/core_components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AgGridReact } from '@ag-grid-community/react'
import { toPastTenseAction } from '../../../../utils/actionLabel'
import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import PlanOverridesTabsHeader from '../../../../components/PlanOverridesTabsHeader'
import GlobalOverrideFilter from '../../../../components/GlobalOverrideFilter'
import OverrideTabLayout from '../../../../components/OverrideTabLayout'
import StatusSubTabs from '../../../../components/StatusSubTabs'
import { PLAN_OVERRIDE_TAB_KEYS } from '../../../../constants'
import { mapGlobalFilters } from '../../../../hooks/useGlobalFilterMapping'
import { PLAN_MANAGEMENT_V2 } from '../../../../../../paths'
import SmokerRateOverrideRulesGrid from '../../components/SmokerRateOverrideRulesGrid'
import type { SelectionMode } from '../../../../components/ThreeStateHeaderCheckbox'
import NewPlanSmokerRateOverrideModal from '../../components/NewPlanSmokerRateOverrideModal'
import PlanSmokerRateOverrideModal, {
  type PlanSmokerRateOverrideModalMode,
} from '../../components/PlanSmokerRateOverrideModal'
import type {
  PlanSmokerRateOverrideRuleResponseDto,
  PlanSmokerRateOverrideRuleSortField,
  SortOrder,
} from '../../../../api/dto/plan-smoker-rate-override-rules.dto'
import { PlanSmokerRateOverrideRuleStatus } from '../../enums/plan-smoker-rate-override-rule.enum'
import { listSmokerRateOverrideRules } from '../../../../api/plan-smoker-rate-override-rules-api'
import { listBulkImportSessions } from '../../../../api/plan-override-bulk-imports-api'
import { BulkImportStatus } from '../../../../api/dto/bulk-import-session.dto'
import { planSmokerRateOverridesPath } from '../../paths'
import {
  useGetSmokerRateOverrideRules,
  SMOKER_RATE_OVERRIDE_RULES_QUERY_KEY,
} from './hooks/useGetSmokerRateOverrideRules'
import { useOverrideQueryParams } from '../../../../hooks/useOverrideQueryParams'
import { useToggleSmokerRateOverrideRuleStatus } from '../../hooks/useToggleSmokerRateOverrideRuleStatus'
import { useDeleteSmokerRateOverrideRule } from '../../hooks/useDeleteSmokerRateOverrideRule'
import { useBulkDeleteSmokerRateOverrideRules } from '../../hooks/useBulkDeleteSmokerRateOverrideRules'
import { useBulkUpdateSmokerRateStatusByIds } from '../../hooks/useBulkUpdateSmokerRateStatusByIds'
import { useDeleteSmokerRateOverrideRulesByFilter } from '../../hooks/useDeleteSmokerRateOverrideRulesByFilter'
import { useUpdateSmokerRateStatusByFilter } from '../../hooks/useUpdateSmokerRateStatusByFilter'
import BulkImportCsvModal from '../../../../components/BulkImportCsvModal'
import BulkImportSessionsContent from '../../../../components/BulkImportSessionsContent'
import { useBulkImportSmokerRateCsv } from '../../hooks/useBulkImportSmokerRateCsv'
import { BULK_IMPORT_SESSIONS_QUERY_KEY } from '../../../PlanBenefitOverrides/hooks/useBulkImportCsv'
import { useBulkImportSessionPolling } from '../../../PlanBenefitOverrides/hooks/useBulkImportSessionPolling'
import { getApiErrorStatus } from '../../../../utils/apiErrors'
import SmokerRateLevelHistoryContent from '../../components/SmokerRateLevelHistoryContent'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import '../../../PlanBenefitOverrides/PlanBenefitOverrides.scss'
import { downloadSmokerRateTemplate } from '../../utils/downloadSmokerRateTemplate'
import AutoApproveSwitch from '../../../../components/AutoApproveSwitch'

const HISTORY_SUBTAB_KEY = 'HISTORY' as const
const BULK_IMPORT_SUBTAB_KEY = 'BULK_IMPORT' as const
type SubTabKey =
  | PlanSmokerRateOverrideRuleStatus
  | typeof HISTORY_SUBTAB_KEY
  | typeof BULK_IMPORT_SUBTAB_KEY

const STATUS_TAB_ITEMS: { key: SubTabKey; label: string }[] = [
  { key: PlanSmokerRateOverrideRuleStatus.DRAFT, label: 'Draft' },
  { key: PlanSmokerRateOverrideRuleStatus.APPROVED, label: 'Approved' },
  { key: PlanSmokerRateOverrideRuleStatus.REJECTED, label: 'Rejected' },
  { key: HISTORY_SUBTAB_KEY, label: 'History' },
  { key: BULK_IMPORT_SUBTAB_KEY, label: 'Bulk Import' },
]

const DEFAULT_PAGE_SIZE = 20

const VALID_SUBTAB_KEYS = new Set<string>([
  PlanSmokerRateOverrideRuleStatus.DRAFT,
  PlanSmokerRateOverrideRuleStatus.APPROVED,
  PlanSmokerRateOverrideRuleStatus.REJECTED,
  HISTORY_SUBTAB_KEY,
  BULK_IMPORT_SUBTAB_KEY,
])

function parseInitialStatus(subtab?: string): SubTabKey {
  const raw = subtab?.toUpperCase()
  if (raw && VALID_SUBTAB_KEYS.has(raw)) return raw as SubTabKey
  return PlanSmokerRateOverrideRuleStatus.DRAFT
}

interface ListPlanSmokerRateOverrideProps {
  params?: { subtab?: string }
}

export default function ListPlanSmokerRateOverride({
  params,
}: ListPlanSmokerRateOverrideProps) {
  const queryClient = useQueryClient()
  const {
    parsed,
    updateQuery,
    navigateSubtab,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
  } = useOverrideQueryParams()

  const [activeStatus, setActiveStatus] = useState<SubTabKey>(() =>
    parseInitialStatus(params?.subtab)
  )
  const [historyTotal, setHistoryTotal] = useState(0)
  const [bulkImportTotal, setBulkImportTotal] = useState(0)
  const isHistoryTab = activeStatus === HISTORY_SUBTAB_KEY
  const isBulkImportTab = activeStatus === BULK_IMPORT_SUBTAB_KEY
  const isOverrideTab = !isHistoryTab && !isBulkImportTab
  const [total, setTotal] = useState(0)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false)
  const [overrideModal, setOverrideModal] = useState<{
    ruleId: string
    mode: PlanSmokerRateOverrideModalMode
  } | null>(() =>
    parsed.ruleId ? { ruleId: parsed.ruleId, mode: parsed.mode } : null
  )
  // Keep overrideModal in sync with URL so browser back/forward re-opens the modal.
  useEffect(() => {
    setOverrideModal(
      parsed.ruleId ? { ruleId: parsed.ruleId, mode: parsed.mode } : null
    )
  }, [parsed.ruleId, parsed.mode])

  const globalFilters = mapGlobalFilters({
    state: parsed.state,
    issuerId: parsed.issuerId,
    hiosPlanId: parsed.hiosPlanId,
  })
  const mappedFilter = globalFilters.smokerRates

  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none')
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([])
  const gridRef =
    useRef<AgGridReact<PlanSmokerRateOverrideRuleResponseDto>>(null)
  const programmaticSelectRef = useRef(false)

  const [pollingSessionId, setPollingSessionId] = useState<string | null>(null)
  const [isLockDismissed, setIsLockDismissed] = useState(false)
  const [bulkImportRefreshSignal, setBulkImportRefreshSignal] = useState(0)
  const [bulkImportProgressSignal, setBulkImportProgressSignal] = useState(0)
  const prevPollingStatusRef = useRef<string | null>(null)

  const { data: polledSession } = useBulkImportSessionPolling(pollingSessionId)

  const { data: latestSessions } = useQuery({
    queryKey: [BULK_IMPORT_SESSIONS_QUERY_KEY, 'smoker-rates', 'mount-check'],
    queryFn: () =>
      listBulkImportSessions({
        page: 1,
        limit: 1,
        overrideType: 'smoker-rates',
      }),
    staleTime: 30_000,
  })

  // Only run when latestSessions loads; pollingSessionId check prevents loop
  useEffect(() => {
    const latest = latestSessions?.data[0]
    if (
      latest &&
      latest.status === BulkImportStatus.PROCESSING &&
      !pollingSessionId
    ) {
      prevPollingStatusRef.current = BulkImportStatus.PROCESSING
      setPollingSessionId(latest.id)
    }
  }, [latestSessions])

  useEffect(() => {
    if (!polledSession) return
    const prev = prevPollingStatusRef.current
    prevPollingStatusRef.current = polledSession.status

    if (prev !== BulkImportStatus.PROCESSING) return

    if (polledSession.status === BulkImportStatus.COMPLETED) {
      const created = polledSession.createdCount ?? 0
      const updated = polledSession.updatedCount ?? 0
      const errors = polledSession.errorCount ?? 0
      let msg = `Import completed: ${created} created, ${updated} updated`
      if (errors > 0) msg += `. ${errors} error(s) — download to view.`
      message.success(msg)
      setPollingSessionId(null)
      setIsLockDismissed(true)
      prevPollingStatusRef.current = null
      setBulkImportRefreshSignal((n) => n + 1)
      queryClient.invalidateQueries({
        queryKey: [
          BULK_IMPORT_SESSIONS_QUERY_KEY,
          'smoker-rates',
          'mount-check',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [SMOKER_RATE_OVERRIDE_RULES_QUERY_KEY],
      })
      gridRef.current?.api?.refreshServerSide({ purge: true })
    } else if (polledSession.status === BulkImportStatus.FAILED) {
      message.error('Import failed')
      setPollingSessionId(null)
      setIsLockDismissed(true)
      prevPollingStatusRef.current = null
      setBulkImportRefreshSignal((n) => n + 1)
      queryClient.invalidateQueries({
        queryKey: [
          BULK_IMPORT_SESSIONS_QUERY_KEY,
          'smoker-rates',
          'mount-check',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [SMOKER_RATE_OVERRIDE_RULES_QUERY_KEY],
      })
      gridRef.current?.api?.refreshServerSide({ purge: true })
    } else if (polledSession.status === BulkImportStatus.PROCESSING) {
      setBulkImportProgressSignal((n) => n + 1)
    }
  }, [polledSession, queryClient])

  const lockSession = polledSession ?? latestSessions?.data[0] ?? null
  const hasProcessingImport =
    !isLockDismissed &&
    lockSession !== null &&
    lockSession.status === BulkImportStatus.PROCESSING

  const handleDismissLock = useCallback(() => {
    setIsLockDismissed(true)
  }, [])

  const handleTabChange = useCallback(
    (key: SubTabKey) => {
      setActiveStatus(key)
      setTotal(0)
      setSelectionMode('none')
      setSelectedPageIds([])
      setOverrideModal(null)
      gridRef.current?.api?.deselectAll()
      navigateSubtab(planSmokerRateOverridesPath(key))
    },
    [navigateSubtab]
  )

  useEffect(() => {
    const nextStatus = parseInitialStatus(params?.subtab)
    setActiveStatus(nextStatus)
    setTotal(0)
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [params?.subtab])

  useEffect(() => {
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [parsed.state, parsed.issuerId, parsed.hiosPlanId])

  const draftQuery = useGetSmokerRateOverrideRules({
    status: PlanSmokerRateOverrideRuleStatus.DRAFT,
    ...mappedFilter,
    page: 1,
    limit: 1,
  })
  const approvedQuery = useGetSmokerRateOverrideRules({
    status: PlanSmokerRateOverrideRuleStatus.APPROVED,
    ...mappedFilter,
    page: 1,
    limit: 1,
  })
  const rejectedQuery = useGetSmokerRateOverrideRules({
    status: PlanSmokerRateOverrideRuleStatus.REJECTED,
    ...mappedFilter,
    page: 1,
    limit: 1,
  })

  const datasource = useMemo<IServerSideDatasource>(() => {
    if (!isOverrideTab) return { getRows: () => undefined }
    return {
      getRows: async (
        params: IServerSideGetRowsParams<PlanSmokerRateOverrideRuleResponseDto>
      ) => {
        try {
          const { startRow, endRow, sortModel } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1
          const sort = sortModel?.[0]
          const sortBy = sort?.colId as
            | PlanSmokerRateOverrideRuleSortField
            | undefined
          const sortOrder = sort?.sort as SortOrder | undefined
          const result = await listSmokerRateOverrideRules({
            status: activeStatus as PlanSmokerRateOverrideRuleStatus,
            ...mappedFilter,
            page,
            limit: pageSize,
            ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          })
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch {
          params.fail()
        }
      },
    }
  }, [activeStatus, mappedFilter.state, mappedFilter.issuerId, isOverrideTab])

  const isMultiPage = total > DEFAULT_PAGE_SIZE

  const subTabItems = useMemo(
    () =>
      STATUS_TAB_ITEMS.map((tab) => {
        if (tab.key === PlanSmokerRateOverrideRuleStatus.DRAFT) {
          return { ...tab, count: draftQuery.total }
        }
        if (tab.key === PlanSmokerRateOverrideRuleStatus.APPROVED) {
          return { ...tab, count: approvedQuery.total }
        }
        if (tab.key === PlanSmokerRateOverrideRuleStatus.REJECTED) {
          return { ...tab, count: rejectedQuery.total }
        }
        if (tab.key === BULK_IMPORT_SUBTAB_KEY) {
          return { ...tab, count: bulkImportTotal || undefined }
        }
        return tab
      }),
    [
      draftQuery.total,
      approvedQuery.total,
      rejectedQuery.total,
      bulkImportTotal,
    ]
  )

  const { mutateAsync: importCsv, isPending: isBulkImporting } =
    useBulkImportSmokerRateCsv()

  const handleNewOverride = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const handleBulkImportOpen = useCallback(() => {
    setIsBulkImportOpen(true)
  }, [])

  const handleBulkImportClose = useCallback(() => {
    setIsBulkImportOpen(false)
  }, [])

  const handleImport = useCallback(
    async (file: File) => {
      const response = await importCsv({ file })
      message.info('Import started. Track progress in the Bulk Import tab.')
      setPollingSessionId(response.id)
      setIsLockDismissed(false)
      prevPollingStatusRef.current = BulkImportStatus.PROCESSING
      handleTabChange(BULK_IMPORT_SUBTAB_KEY)
      setBulkImportRefreshSignal((n) => n + 1)
      return response
    },
    [importCsv, handleTabChange]
  )

  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  const handleSwitchToRuleTab = useCallback(
    (status: PlanSmokerRateOverrideRuleStatus) => {
      if (status !== activeStatus) {
        handleTabChange(status)
      } else {
        gridRef.current?.api?.refreshServerSide({ purge: true })
      }
    },
    [activeStatus, handleTabChange]
  )

  const handleRowView = useCallback(
    (ruleId: string) => {
      setOverrideModal({ ruleId, mode: 'view' })
      updateQuery({ ruleId, mode: 'view' })
    },
    [updateQuery]
  )

  const handleRowEdit = useCallback(
    (ruleId: string) => {
      setOverrideModal({ ruleId, mode: 'edit' })
      updateQuery({ ruleId, mode: 'edit' })
    },
    [updateQuery]
  )

  const { mutateAsync: toggleStatus } = useToggleSmokerRateOverrideRuleStatus()
  const { mutateAsync: deleteRule } = useDeleteSmokerRateOverrideRule()
  const { mutateAsync: bulkDelete } = useBulkDeleteSmokerRateOverrideRules()
  const { mutateAsync: deleteByFilter } =
    useDeleteSmokerRateOverrideRulesByFilter()
  const { mutateAsync: bulkUpdateStatus } = useBulkUpdateSmokerRateStatusByIds()
  const { mutateAsync: updateByFilter } = useUpdateSmokerRateStatusByFilter()

  const handleRowDelete = useCallback(
    (ruleId: string) => {
      Modal.confirm({
        title: 'Delete override',
        content:
          'Are you sure you want to delete this override? This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await deleteRule(ruleId)
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success('Override deleted.')
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('delete smoker rate override failed', err)
            message.error('Failed to delete override. Please try again.')
          }
        },
      })
    },
    [deleteRule]
  )

  const handleRowToggleStatus = useCallback(
    (ruleId: string, toStatus: PlanSmokerRateOverrideRuleStatus) => {
      const actionLabel =
        toStatus === PlanSmokerRateOverrideRuleStatus.APPROVED
          ? 'Approve'
          : toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED
            ? 'Reject'
            : 'Re-draft'
      Modal.confirm({
        title: `${actionLabel} override`,
        content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
        okText: actionLabel,
        okButtonProps: {
          danger: toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED,
        },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await toggleStatus({ id: ruleId, status: toStatus })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              `${actionLabel.toLowerCase()} smoker rate override failed`,
              err
            )
            message.error(
              `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
            )
          }
        },
      })
    },
    [toggleStatus]
  )

  const handleRowApprove = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanSmokerRateOverrideRuleStatus.APPROVED),
    [handleRowToggleStatus]
  )

  const handleRowReject = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanSmokerRateOverrideRuleStatus.REJECTED),
    [handleRowToggleStatus]
  )

  const handleRowRedraft = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanSmokerRateOverrideRuleStatus.DRAFT),
    [handleRowToggleStatus]
  )

  const handleHeaderCheckboxClicked = useCallback(() => {
    if (selectionMode === 'none') {
      programmaticSelectRef.current = true
      gridRef.current?.api?.selectAll()
      const ids: string[] = []
      gridRef.current?.api?.forEachNode((node) => {
        if (node.data?.id) ids.push(node.data.id as string)
      })
      setSelectedPageIds(ids)
      setSelectionMode('page')
    } else if (selectionMode === 'page' && isMultiPage) {
      setSelectionMode('all')
    } else {
      gridRef.current?.api?.deselectAll()
      setSelectedPageIds([])
      setSelectionMode('none')
    }
  }, [selectionMode, isMultiPage])

  const handleGridSelectionChanged = useCallback((ids: string[]) => {
    setSelectedPageIds(ids)
    setSelectionMode(ids.length === 0 ? 'none' : 'page')
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectionMode('all')
  }, [])

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll()
    setSelectedPageIds([])
    setSelectionMode('none')
  }, [])

  const initialSortModel = useMemo(
    () => ({ colId: parsed.sortBy, sort: parsed.sortOrder }),
    [parsed.sortBy, parsed.sortOrder]
  )

  const handlePageChanged = useCallback(() => {
    setSelectionMode((prev) => (prev === 'all' ? 'all' : 'none'))
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [])

  const handleBulkDelete = useCallback(() => {
    if (selectionMode === 'none') return

    if (selectionMode === 'all') {
      if (!isOverrideTab) return
      const statusFilter = activeStatus as PlanSmokerRateOverrideRuleStatus
      const statusLabel = statusFilter.toLowerCase()
      const hasFilter = mappedFilter.state || mappedFilter.issuerId
      Modal.confirm({
        title: hasFilter
          ? 'Delete all matching overrides'
          : 'Delete all overrides',
        content: `Delete all ${total} override(s) in the ${statusLabel} tab? This cannot be undone.`,
        okText: 'Delete All',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            const result = await deleteByFilter({
              status: statusFilter,
              ...mappedFilter,
            })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(`${result.deletedCount} override(s) deleted.`)
            setSelectionMode('none')
            setSelectedPageIds([])
          } catch (err: unknown) {
            // eslint-disable-next-line no-console
            console.error('bulk delete all smoker rate overrides failed', err)
            if (getApiErrorStatus(err) === 422) {
              message.error(
                'Filter matches more than 500 rules. Narrow the filter before deleting.'
              )
            } else {
              message.error('Failed to delete overrides. Please try again.')
            }
          }
        },
      })
      return
    }

    if (selectedPageIds.length === 0) return
    Modal.confirm({
      title: 'Delete selected overrides',
      content: `Delete ${selectedPageIds.length} selected override(s)? This cannot be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Close',
      closable: true,
      onOk: async () => {
        try {
          await bulkDelete({ ids: selectedPageIds })
          gridRef.current?.api?.refreshServerSide({ purge: true })
          message.success(`${selectedPageIds.length} override(s) deleted.`)
          setSelectionMode('none')
          setSelectedPageIds([])
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('bulk delete smoker rate overrides failed', err)
          message.error('Failed to delete overrides. Please try again.')
        }
      },
    })
  }, [
    selectionMode,
    selectedPageIds,
    isOverrideTab,
    activeStatus,
    mappedFilter,
    total,
    bulkDelete,
    deleteByFilter,
  ])

  const handleBulkStatusChange = useCallback(
    (toStatus: PlanSmokerRateOverrideRuleStatus, label: string) => {
      if (selectionMode === 'none') return

      if (selectionMode === 'all') {
        if (!isOverrideTab) return
        const statusFilter = activeStatus as PlanSmokerRateOverrideRuleStatus
        const statusLabel = statusFilter.toLowerCase()
        const hasFilter = mappedFilter.state || mappedFilter.issuerId
        Modal.confirm({
          title: hasFilter
            ? `${label} all matching overrides`
            : `${label} all overrides`,
          content: `${label} all ${total} override(s) in the ${statusLabel} tab?`,
          okText: label,
          okButtonProps: {
            danger: toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED,
          },
          cancelText: 'Close',
          closable: true,
          onOk: async () => {
            try {
              const result = await updateByFilter({
                query: {
                  status: statusFilter,
                  ...mappedFilter,
                },
                targetStatus: toStatus,
              })
              gridRef.current?.api?.refreshServerSide({ purge: true })
              message.success(
                `${result.updatedCount} override(s) ${toPastTenseAction(label)}.`
              )
              setSelectionMode('none')
              setSelectedPageIds([])
            } catch (err: unknown) {
              // eslint-disable-next-line no-console
              console.error(
                'bulk status change (filter) smoker rate overrides failed',
                err
              )
              if (getApiErrorStatus(err) === 422) {
                message.error(
                  'Filter matches more than 500 rules. Narrow the filter before updating.'
                )
              } else {
                message.error('Failed to update overrides. Please try again.')
              }
            }
          },
        })
        return
      }

      if (selectedPageIds.length === 0) return
      Modal.confirm({
        title: `${label} selected overrides`,
        content: `${label} ${selectedPageIds.length} selected override(s)?`,
        okText: label,
        okButtonProps: {
          danger: toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED,
        },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await bulkUpdateStatus({ ids: selectedPageIds, status: toStatus })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(
              `${selectedPageIds.length} override(s) ${toPastTenseAction(label)}.`
            )
            setSelectionMode('none')
            setSelectedPageIds([])
          } catch (err: unknown) {
            // eslint-disable-next-line no-console
            console.error(
              'bulk status change (ids) smoker rate overrides failed',
              err
            )
            if (getApiErrorStatus(err) === 404) {
              message.error('One or more selected rules could not be found.')
            } else {
              message.error('Failed to update overrides. Please try again.')
            }
          }
        },
      })
    },
    [
      selectionMode,
      selectedPageIds,
      isOverrideTab,
      activeStatus,
      mappedFilter,
      total,
      bulkUpdateStatus,
      updateByFilter,
    ]
  )

  const handleGlobalStateChange = useCallback(
    (value: string | undefined) => {
      updateQuery({ state: value ?? '', page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalIssuerIdChange = useCallback(
    (value: string) => {
      updateQuery({ issuerId: value, page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalHiosPlanIdChange = useCallback(
    (value: string) => {
      updateQuery({ hiosPlanId: value, page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalClearAll = useCallback(() => {
    updateQuery({ state: '', issuerId: '', hiosPlanId: '', page: '1' })
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [updateQuery])

  const handleOverrideModalModeChange = useCallback(
    (mode: PlanSmokerRateOverrideModalMode) => {
      setOverrideModal((prev) => (prev ? { ...prev, mode } : prev))
      updateQuery({ mode })
    },
    [updateQuery]
  )

  const handleOverrideModalClose = useCallback(() => {
    setOverrideModal(null)
    updateQuery({ ruleId: '', mode: '' })
  }, [updateQuery])

  return (
    <Page
      title="Plan Override"
      horizontalRule
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Plans', path: `/${PLAN_MANAGEMENT_V2}` },
            { title: 'Plan Information', path: `/${PLAN_MANAGEMENT_V2}` },
            { title: 'Plan Override', path: planSmokerRateOverridesPath() },
          ]}
        />
      }
    >
      <p className="subtitle">
        Configure smoker rate overrides that modify plan data during the review
        process.
      </p>

      {hasProcessingImport && (
        <Alert
          type="info"
          showIcon
          message="A bulk import is currently processing. Editing is disabled until it completes."
          action={
            <Button size="small" onClick={handleDismissLock}>
              Dismiss
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <GlobalOverrideFilter
        state={parsed.state}
        issuerId={parsed.issuerId}
        hiosPlanId={parsed.hiosPlanId}
        onStateChange={handleGlobalStateChange}
        onIssuerIdChange={handleGlobalIssuerIdChange}
        onHiosPlanIdChange={handleGlobalHiosPlanIdChange}
        onClearAll={handleGlobalClearAll}
      />

      <PlanOverridesTabsHeader
        activeTab={PLAN_OVERRIDE_TAB_KEYS.SMOKER_RATES}
      />

      <OverrideTabLayout
        recordLabel={
          isHistoryTab
            ? 'History'
            : isBulkImportTab
              ? 'Bulk Import'
              : 'Smoker Rates — Override'
        }
        recordCount={
          isHistoryTab
            ? historyTotal
            : isBulkImportTab
              ? bulkImportTotal
              : total
        }
        actionsSlot={
          !isHistoryTab ? (
            <>
              <AutoApproveSwitch settingsKey="smoker-rates" />
              {isBulkImporting && (
                <span className="importingIndicator">
                  <LoadingOutlined spin />
                  Importing…
                </span>
              )}
              <Button
                icon={<UploadOutlined />}
                onClick={handleBulkImportOpen}
                disabled={isBulkImporting || hasProcessingImport}
              >
                Bulk Import
              </Button>
              {!isBulkImportTab && (
                <Button
                  type="primary"
                  onClick={handleNewOverride}
                  disabled={hasProcessingImport}
                >
                  + New override
                </Button>
              )}
            </>
          ) : undefined
        }
        subTabsSlot={
          <StatusSubTabs
            items={subTabItems}
            activeKey={activeStatus}
            onChange={handleTabChange}
          />
        }
        toolbarSlot={
          isOverrideTab && selectionMode !== 'none' ? (
            <>
              {activeStatus === PlanSmokerRateOverrideRuleStatus.DRAFT && (
                <>
                  <Button
                    type="primary"
                    onClick={() =>
                      handleBulkStatusChange(
                        PlanSmokerRateOverrideRuleStatus.APPROVED,
                        'Approve'
                      )
                    }
                  >
                    {selectionMode === 'all'
                      ? `Approve all (${total})`
                      : `Approve selected (${selectedPageIds.length})`}
                  </Button>
                  <Button
                    danger
                    onClick={() =>
                      handleBulkStatusChange(
                        PlanSmokerRateOverrideRuleStatus.REJECTED,
                        'Reject'
                      )
                    }
                  >
                    {selectionMode === 'all'
                      ? `Reject all (${total})`
                      : `Reject selected (${selectedPageIds.length})`}
                  </Button>
                </>
              )}
              {activeStatus === PlanSmokerRateOverrideRuleStatus.APPROVED && (
                <>
                  <Button
                    onClick={() =>
                      handleBulkStatusChange(
                        PlanSmokerRateOverrideRuleStatus.DRAFT,
                        'Draft'
                      )
                    }
                  >
                    {selectionMode === 'all'
                      ? `Draft all (${total})`
                      : `Draft selected (${selectedPageIds.length})`}
                  </Button>
                  <Button
                    danger
                    onClick={() =>
                      handleBulkStatusChange(
                        PlanSmokerRateOverrideRuleStatus.REJECTED,
                        'Reject'
                      )
                    }
                  >
                    {selectionMode === 'all'
                      ? `Reject all (${total})`
                      : `Reject selected (${selectedPageIds.length})`}
                  </Button>
                </>
              )}
              {activeStatus === PlanSmokerRateOverrideRuleStatus.REJECTED && (
                <Button danger onClick={handleBulkDelete}>
                  {selectionMode === 'all'
                    ? `Delete all (${total})`
                    : `Delete selected (${selectedPageIds.length})`}
                </Button>
              )}
            </>
          ) : undefined
        }
        contentSlot={
          isHistoryTab ? (
            <SmokerRateLevelHistoryContent
              onTotalChange={setHistoryTotal}
              defaultPageSize={parsed.pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : isBulkImportTab ? (
            <BulkImportSessionsContent
              overrideType="smoker-rates"
              onTotalChange={setBulkImportTotal}
              refreshSignal={bulkImportRefreshSignal}
              pollingRefreshSignal={bulkImportProgressSignal}
              defaultPageSize={parsed.pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : (
            <SmokerRateOverrideRulesGrid
              ref={gridRef}
              key={activeStatus}
              datasource={datasource}
              defaultPageSize={parsed.pageSize}
              initialPage={parsed.page}
              initialSortModel={initialSortModel}
              selectionMode={selectionMode}
              selectedCount={selectedPageIds.length}
              totalCount={total}
              isMultiPage={isMultiPage}
              programmaticSelectRef={programmaticSelectRef}
              onRowView={handleRowView}
              onRowEdit={hasProcessingImport ? undefined : handleRowEdit}
              onRowApprove={hasProcessingImport ? undefined : handleRowApprove}
              onRowReject={hasProcessingImport ? undefined : handleRowReject}
              onRowRedraft={hasProcessingImport ? undefined : handleRowRedraft}
              onRowDelete={hasProcessingImport ? undefined : handleRowDelete}
              onSelectionChanged={handleGridSelectionChanged}
              onHeaderCheckboxClicked={handleHeaderCheckboxClicked}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onPageChanged={handlePageChanged}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
            />
          )
        }
      />

      <NewPlanSmokerRateOverrideModal
        open={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onCreated={handleSwitchToRuleTab}
        onRequestEdit={handleRowEdit}
      />

      <PlanSmokerRateOverrideModal
        ruleId={overrideModal?.ruleId ?? null}
        initialMode={overrideModal?.mode ?? 'view'}
        onClose={handleOverrideModalClose}
        onModeChange={handleOverrideModalModeChange}
        onSaved={hasProcessingImport ? undefined : handleSwitchToRuleTab}
        canEdit={!hasProcessingImport}
      />

      <BulkImportCsvModal
        open={isBulkImportOpen}
        onClose={handleBulkImportClose}
        onImport={handleImport}
        onDownloadTemplate={downloadSmokerRateTemplate}
      />
    </Page>
  )
}
